import { useMutation, useQuery } from '@base-app/web2'
import { memo, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { VIDEO_PLAYLISTS_QUERY } from '../../../graphql/videoPlaylists/queries'
import { CREATE_VIDEO, UPDATE_VIDEO } from '../../../graphql/videos/mutations'
import { VIDEO_QUERY } from '../../../graphql/videos/queries'
import type { Video } from '../../../graphql/videos/types'
import { useRecordById } from '../../../hooks/useRecordById'
import { useToast } from '../../../hooks/useToast'
import { prependToLists } from '../../../lib/cacheLists'
import { isWebUrl } from '../../../services/url-validation'
import { Button, Checkbox, FormActions, Select, type SelectOption } from '../../atoms'
import { FormField, FormLoadState, TextareaField } from '../../molecules'

interface VideoFormFieldsProps {
  video?: Video
  clientId: string
  appId: string
  onSuccess: () => void
  onClose: () => void
  // Renders every field as its pulsing placeholder while the record loads
  skeleton?: boolean
}

export const VideoFormFields = ({
  video,
  clientId,
  appId,
  onSuccess,
  onClose,
  skeleton,
}: VideoFormFieldsProps) => {
  const { t } = useTranslation('admin-mobifull')
  const { showToast } = useToast()
  const isEdit = !!video
  const [name, setName] = useState(video?.name ?? '')
  const [embeddedLink, setEmbeddedLink] = useState(video?.embedded_link ?? '')
  const [playlistId, setPlaylistId] = useState(video?.playlist_id ?? '')
  const [observations, setObservations] = useState(video?.observations ?? '')
  const [live, setLive] = useState(video?.live ?? false)
  const [localLive, setLocalLive] = useState(video?.local_live ?? false)
  const [liveHours, setLiveHours] = useState(video?.live_hours?.toString() ?? '')
  const [liveTimeout, setLiveTimeout] = useState(video?.live_timeout?.toString() ?? '')
  const [liveSecureUrl, setLiveSecureUrl] = useState(video?.live_secure_url ?? '')
  const [liveInputUri, setLiveInputUri] = useState(video?.live_input_uri ?? '')
  const [liveOutputUri, setLiveOutputUri] = useState(video?.live_output_uri ?? '')
  const [livePublicId, setLivePublicId] = useState(video?.live_public_id ?? '')
  const [errors, setErrors] = useState<{ name?: string; embeddedLink?: string }>({})

  const { data: playlistsData } = useQuery(VIDEO_PLAYLISTS_QUERY, {
    variables: { clientId, applicationId: appId, limit: 999 },
  })
  const playlists = playlistsData?.videoPlaylists ?? []

  const playlistOptions: SelectOption[] = useMemo(
    () => [
      { value: '', label: t('videos.form.noPlaylist') },
      ...playlists.map(p => ({ value: p.id, label: p.name })),
    ],
    [playlists, t],
  )

  const makeOptimisticVideo = (id: string): Video => ({
    __typename: 'Video',
    id,
    client_id: clientId,
    application_id: appId,
    playlist_id: playlistId || null,
    name,
    embedded_link: embeddedLink || null,
    observations: observations || null,
    thumbnail: null,
    live,
    local_live: localLive,
    live_hours: liveHours ? Number(liveHours) : null,
    live_timeout: liveTimeout ? Number(liveTimeout) : null,
    live_secure_url: liveSecureUrl || null,
    live_input_uri: liveInputUri || null,
    live_output_uri: liveOutputUri || null,
    live_public_id: livePublicId || null,
    synchronized_at: null,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  })

  const [createVideo, { loading: creating }] = useMutation(CREATE_VIDEO, {
    optimisticResponse: { createVideo: makeOptimisticVideo(`temp-${Date.now()}`) },
    update(cache, result) {
      const newVideo = result.data?.createVideo
      if (!newVideo) {
        return
      }
      prependToLists(cache, 'videos', newVideo, { clientId, applicationId: appId })
    },
    onError(err) {
      showToast(err.message, 'error')
    },
    onCompleted() {
      onSuccess()
    },
  })

  const [updateVideo, { loading: updating }] = useMutation(UPDATE_VIDEO, {
    optimisticResponse: { updateVideo: makeOptimisticVideo(video?.id ?? '') },
    onError(err) {
      showToast(err.message, 'error')
    },
    onCompleted() {
      onSuccess()
    },
  })

  const loading = creating || updating

  function validate() {
    const errs: { name?: string; embeddedLink?: string } = {}
    if (!name.trim()) {
      errs.name = t('common.form.nameRequired')
    }
    if (!isWebUrl(embeddedLink.trim())) {
      errs.embeddedLink = t('common.form.httpsUrlInvalid')
    }
    setErrors(errs)
    return Object.keys(errs).length === 0
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!validate()) {
      return
    }
    const vars = {
      playlist_id: playlistId || null,
      name,
      embedded_link: embeddedLink || null,
      observations: observations || null,
      live,
      local_live: localLive,
      live_hours: liveHours ? Number(liveHours) : null,
      live_timeout: liveTimeout ? Number(liveTimeout) : null,
      live_secure_url: liveSecureUrl || null,
      live_input_uri: liveInputUri || null,
      live_output_uri: liveOutputUri || null,
      live_public_id: livePublicId || null,
    }
    if (isEdit) {
      updateVideo({ variables: { id: video.id, ...vars } })
    } else {
      createVideo({ variables: { client_id: clientId, application_id: appId, ...vars } })
    }
  }

  return (
    <form onSubmit={handleSubmit} noValidate aria-busy={skeleton}>
      {skeleton ? (
        <p className="sr-only" role="status">
          {t('common.loading')}
        </p>
      ) : null}
      <FormField
        skeleton={skeleton}
        id="video-name"
        label={t('videos.form.nameLabel')}
        name="name"
        value={name}
        onChange={e => setName(e.target.value)}
        error={errors.name}
        required
      />
      <div className="form-group">
        <FormField
          skeleton={skeleton}
          id="video-embedded-link"
          label={t('videos.form.embeddedLinkLabel')}
          name="embedded_link"
          value={embeddedLink}
          onChange={e => setEmbeddedLink(e.target.value)}
          error={errors.embeddedLink}
          placeholder="https://"
        />
        <Button
          skeleton={skeleton}
          type="button"
          variant="secondary"
          disabled={!embeddedLink || !isWebUrl(embeddedLink.trim())}
          onClick={() => window.open(embeddedLink, '_blank', 'noopener')}>
          {t('videos.form.previewButton')}
        </Button>
      </div>
      <div className="form-group">
        <label htmlFor="video-playlist">{t('videos.form.playlistLabel')}</label>
        <Select
          skeleton={skeleton}
          id="video-playlist"
          value={playlistId}
          onChange={setPlaylistId}
          options={playlistOptions}
        />
      </div>
      <TextareaField
        skeleton={skeleton}
        id="video-observations"
        label={t('videos.form.observationsLabel')}
        name="observations"
        value={observations}
        onChange={e => setObservations(e.target.value)}
      />
      <details>
        <summary>{t('videos.form.liveSettingsTitle')}</summary>
        <div className="form-group">
          <label htmlFor="live">
            <Checkbox
              skeleton={skeleton}
              id="live"
              name="live"
              checked={live}
              onChange={e => setLive(e.target.checked)}
            />
            {t('videos.form.liveLabel')}
          </label>
        </div>
        <div className="form-group">
          <label htmlFor="local_live">
            <Checkbox
              skeleton={skeleton}
              id="local_live"
              name="local_live"
              checked={localLive}
              onChange={e => setLocalLive(e.target.checked)}
            />
            {t('videos.form.localLiveLabel')}
          </label>
        </div>
        <FormField
          skeleton={skeleton}
          id="live_hours"
          label={t('videos.form.liveHoursLabel')}
          name="live_hours"
          type="number"
          value={liveHours}
          onChange={e => setLiveHours(e.target.value)}
        />
        <FormField
          skeleton={skeleton}
          id="live_timeout"
          label={t('videos.form.liveTimeoutLabel')}
          name="live_timeout"
          type="number"
          value={liveTimeout}
          onChange={e => setLiveTimeout(e.target.value)}
        />
        <FormField
          skeleton={skeleton}
          id="live_secure_url"
          label={t('videos.form.liveSecureUrlLabel')}
          name="live_secure_url"
          value={liveSecureUrl}
          onChange={e => setLiveSecureUrl(e.target.value)}
        />
        <FormField
          skeleton={skeleton}
          id="live_input_uri"
          label={t('videos.form.liveInputUriLabel')}
          name="live_input_uri"
          value={liveInputUri}
          onChange={e => setLiveInputUri(e.target.value)}
        />
        <FormField
          skeleton={skeleton}
          id="live_output_uri"
          label={t('videos.form.liveOutputUriLabel')}
          name="live_output_uri"
          value={liveOutputUri}
          onChange={e => setLiveOutputUri(e.target.value)}
        />
        <FormField
          skeleton={skeleton}
          id="live_public_id"
          label={t('videos.form.livePublicIdLabel')}
          name="live_public_id"
          value={livePublicId}
          onChange={e => setLivePublicId(e.target.value)}
        />
      </details>
      <FormActions>
        <Button type="button" variant="secondary" onClick={onClose} disabled={loading}>
          {t('common.actions.cancel')}
        </Button>
        <Button skeleton={skeleton} type="submit" disabled={loading}>
          {loading ? t('videos.form.saving') : t('videos.form.save')}
        </Button>
      </FormActions>
    </form>
  )
}

interface VideoFormProps extends Omit<VideoFormFieldsProps, 'video'> {
  // The record to edit, by id; omit it to create a new one
  id?: string
}

// Loads the record by id (from the cache when the list has it, otherwise from the API)
export const VideoForm = memo(({ id, ...props }: VideoFormProps) => {
  const { record, status } = useRecordById(VIDEO_QUERY, id, data => data.video)
  if (status === 'loading') {
    return <VideoFormFields skeleton {...props} />
  }
  if (status !== 'new' && status !== 'ready') {
    return <FormLoadState status={status} onClose={props.onClose} />
  }
  return <VideoFormFields key={id ?? 'new'} video={record} {...props} />
})

VideoForm.displayName = 'VideoForm'
